import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { EventqButton, type EventqButtonVariant } from '../../primitives/button/button';
import { EventqResolveSubmit } from '../resolve-submit/resolve-submit';
import { EventqInput } from '../../primitives/input/input';
import {
  EventqActionableList,
  type EventqActionableListActivateOption,
  type EventqActionableListItem,
} from '../actionable-list/actionable-list';
import { EventqDatetimePicker } from '../datetime-picker/datetime-picker';
import { EventqFileDropzone } from '../file-dropzone/file-dropzone';
import { EventqInfoCard } from '../info-card/info-card';
import { EventqSelect } from '../select/select';
import {
  areRequiredFieldsSatisfied,
  type EventqActionFormActionEvent,
  type EventqActionFormActionableList,
  type EventqActionFormConfig,
  type EventqActionFormField,
  type EventqActionFormFilesEvent,
  type EventqActionFormGroup,
  type EventqActionFormListActivateEvent,
  type EventqActionFormListInfoEvent,
  type EventqActionFormSection,
  type EventqActionFormValues,
} from './action-form.types';

export type {
  EventqActionFormAction,
  EventqActionFormActionEvent,
  EventqActionFormActionableList,
  EventqActionFormActions,
  EventqActionFormConfig,
  EventqActionFormDatetimeField,
  EventqActionFormDropzone,
  EventqActionFormField,
  EventqActionFormFieldWidth,
  EventqActionFormFilesEvent,
  EventqActionFormGroup,
  EventqActionFormInfoCard,
  EventqActionFormListActivateEvent,
  EventqActionFormListInfoEvent,
  EventqActionFormRow,
  EventqActionFormSection,
  EventqActionFormSelectField,
  EventqActionFormTextField,
  EventqActionFormValues,
} from './action-form.types';

export {
  areRequiredFieldsSatisfied,
  collectRequiredFieldKeys,
  isEventqActionFormValuePresent,
} from './action-form.types';

@Component({
  selector: 'eventq-action-form',
  imports: [
    EventqButton,
    EventqInput,
    EventqSelect,
    EventqDatetimePicker,
    EventqFileDropzone,
    EventqInfoCard,
    EventqActionableList,
    EventqResolveSubmit,
  ],
  templateUrl: './action-form.html',
  styleUrl: './action-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqActionForm {
  readonly config = input.required<EventqActionFormConfig>();
  readonly values = input<EventqActionFormValues>({});
  /**
   * When true, replaces the form body with a skeleton. Fields and actions are
   * not rendered so the form is inaccessible until async values are ready.
   */
  readonly loading = input(false);
  /** When true, fields show values but cannot be edited and actions do not emit. */
  readonly readonly = input(false);
  /** Talkdown message options forwarded to actionable-list Activate menus (`null` = no menu). */
  readonly activateOptions = input<readonly EventqActionableListActivateOption[] | null>(null);
  /**
   * When true, renders Figma `Button-Green-Sm` Submit inside the form: one control
   * per contact group, or a single control when the form has no groups.
   */
  readonly inlineSubmit = input(false);
  /** Whole-form submitted (Talkdown / single-submit protocols). */
  readonly submitted = input(false);
  readonly submittedAt = input<string | undefined>(undefined);
  /** Group ids whose per-contact Submit already succeeded. */
  readonly submittedGroupIds = input<readonly string[]>([]);
  /**
   * Submitted stamp per group id. Each contact is submitted on its own, so each keeps
   * its own instant; groups missing from the map fall back to `submittedAt`.
   */
  readonly submittedAtByGroupId = input<Readonly<Record<string, string>>>({});
  /** Extra disable (locked in-flight, Talkdown speakers) on top of required fields. */
  readonly extraSubmitDisabled = input(false);
  /**
   * When set, only this contact group is editable. Later groups stay visible
   * but locked until the earlier group is submitted. Unset keeps every group open.
   */
  readonly activeGroupId = input<string | null>(null);
  /** Optional consumer hook for dropzone side effects. */
  readonly onFilesSelected = input<((event: EventqActionFormFilesEvent) => void) | null>(null);

  readonly valuesChange = output<EventqActionFormValues>();
  readonly action = output<EventqActionFormActionEvent>();
  readonly filesChange = output<EventqActionFormFilesEvent>();
  readonly listActivate = output<EventqActionFormListActivateEvent>();
  readonly listInfo = output<EventqActionFormListInfoEvent>();

  /** Group ids (or `root`) that have attempted submit validation. */
  private readonly submitAttemptedGroups = signal<ReadonlySet<string>>(new Set());
  private lastOutcomeFingerprint = '';

  constructor() {
    effect(() => {
      const fingerprint = outcomeValueFingerprint(this.values());
      untracked(() => {
        if (this.lastOutcomeFingerprint && this.lastOutcomeFingerprint !== fingerprint) {
          this.submitAttemptedGroups.set(new Set());
        }
        this.lastOutcomeFingerprint = fingerprint;
      });
    });
  }

  readonly sections = computed(() => this.config().sections);

  /** When the form includes an actionable list, keep sections mounted so the list can show its own skeleton. */
  readonly hasActionableList = computed(() =>
    this.sections().some(
      (section) =>
        section.type === 'actionableList' ||
        (section.type === 'group' &&
          section.sections.some((groupSection) => groupSection.type === 'actionableList')),
    ),
  );

  readonly hasGroups = computed(() => this.sections().some((section) => section.type === 'group'));

  isGroupSubmitted(groupId: string): boolean {
    return this.submitted() || this.submittedGroupIds().includes(groupId);
  }

  /** A group's own stamp when it has one, so earlier contacts keep their own time. */
  groupSubmittedAt(groupId: string): string | undefined {
    if (!this.isGroupSubmitted(groupId)) {
      return undefined;
    }
    return this.submittedAtByGroupId()[groupId] ?? this.submittedAt();
  }

  isGroupInteractive(groupId: string): boolean {
    if (this.readonly() || this.isGroupSubmitted(groupId)) {
      return false;
    }
    const active = this.activeGroupId();
    return active == null || active === '' || active === groupId;
  }

  isGroupSubmitDisabled(group: EventqActionFormGroup): boolean {
    return (
      !this.isGroupInteractive(group.id) ||
      this.extraSubmitDisabled() ||
      !areRequiredFieldsSatisfied(group.sections, this.values())
    );
  }

  isRootSubmitDisabled(): boolean {
    return (
      this.readonly() ||
      this.extraSubmitDisabled() ||
      !areRequiredFieldsSatisfied(this.sections(), this.values())
    );
  }

  isSubmitAttempted(groupId = 'root'): boolean {
    if (this.readonly()) {
      return false;
    }
    return this.submitAttemptedGroups().has(groupId);
  }

  isFieldDisabled(field: EventqActionFormField, groupId?: string): boolean {
    if (groupId && !this.isGroupInteractive(groupId)) {
      return true;
    }
    return this.readonly() || field.disabled === true || fieldIsReadOnly(field);
  }

  isSelectDisabled(field: EventqActionFormField, groupId?: string): boolean {
    return this.isFieldDisabled(field, groupId);
  }

  stringValue(key: string): string {
    const value = this.values()[key];
    return typeof value === 'string' ? value : '';
  }

  selectValue(key: string): string | null {
    const value = this.values()[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  dateValue(key: string): Date | null {
    const value = this.values()[key];
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  listItems(section: EventqActionFormActionableList): readonly EventqActionableListItem[] {
    const value = this.values()[section.itemsKey];
    if (!Array.isArray(value)) {
      return [];
    }
    return value as EventqActionableListItem[];
  }

  listSearchQuery(section: EventqActionFormActionableList): string {
    if (!section.searchKey) {
      return '';
    }
    return this.stringValue(section.searchKey);
  }

  filesValue(key: string): File[] {
    const value = this.values()[key];
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item): item is File => item instanceof File);
  }

  fieldWidthStyle(field: EventqActionFormField): Record<string, string> {
    const width = field.width;
    if (typeof width === 'number') {
      return { flex: `0 0 ${width}px`, width: `${width}px` };
    }
    return { flex: '1 1 0', minWidth: '0' };
  }

  onFieldValueChange(key: string, value: unknown, groupId?: string): void {
    if (this.readonly() || (groupId && !this.isGroupInteractive(groupId))) {
      return;
    }
    this.valuesChange.emit({
      ...this.values(),
      [key]: value,
    });
  }

  onDatetimeApplied(key: string, value: Date, groupId?: string): void {
    this.onFieldValueChange(key, value, groupId);
  }

  onFilesSelectedForKey(key: string, files: File[], groupId?: string): void {
    if (this.readonly() || (groupId && !this.isGroupInteractive(groupId))) {
      return;
    }
    const event: EventqActionFormFilesEvent = { key, files, groupId };
    this.filesChange.emit(event);
    this.onFilesSelected()?.(event);
    this.onFieldValueChange(key, files);
  }

  onListSearchChange(
    section: EventqActionFormActionableList,
    query: string,
    groupId?: string,
  ): void {
    if (!section.searchKey) {
      return;
    }
    this.onFieldValueChange(section.searchKey, query, groupId);
  }

  onListActivate(
    section: EventqActionFormActionableList,
    event: { id: string; optionId?: string },
    groupId?: string,
  ): void {
    if (groupId && !this.isGroupInteractive(groupId)) {
      return;
    }
    this.listActivate.emit({
      id: event.id,
      key: section.key,
      groupId,
      optionId: event.optionId,
    });
  }

  onListInfo(
    section: EventqActionFormActionableList,
    event: { id: string },
    groupId?: string,
  ): void {
    this.listInfo.emit({ id: event.id, key: section.key, groupId });
  }

  onActionPressed(
    actionId: string,
    validates: boolean | undefined,
    groupSections: readonly EventqActionFormSection[],
    groupId?: string,
  ): void {
    if (this.readonly() || (groupId && !this.isGroupInteractive(groupId))) {
      return;
    }
    const scopeId = groupId ?? 'root';
    const shouldValidate = validates ?? actionId === 'submit';

    if (shouldValidate) {
      this.submitAttemptedGroups.update((prev) => {
        const next = new Set(prev);
        next.add(scopeId);
        return next;
      });

      if (!areRequiredFieldsSatisfied(groupSections, this.values())) {
        return;
      }
    }

    this.action.emit({ id: actionId, groupId });
  }

  actionVariant(variant: EventqButtonVariant | undefined): EventqButtonVariant {
    return variant ?? 'primary-solid';
  }
}

function fieldIsReadOnly(field: EventqActionFormField): boolean {
  return 'readOnly' in field && field.readOnly === true;
}

function outcomeValueFingerprint(values: EventqActionFormValues): string {
  return Object.keys(values)
    .filter((key) => key === 'outcome' || key.startsWith('outcome__'))
    .sort()
    .map((key) => `${key}=${String(values[key] ?? '')}`)
    .join('|');
}
